import { CART_COPY } from '../../features/checkout/checkout-copy';
import { Cart, CartGoneError, CartLine, CartStockError } from '../models/cart.model';
import {
  EarlyAccessBlockedError,
  EarlyAccessGateRejection,
} from '../perron-points/early-access-gate';

/**
 * Coerciona un valor del API a `number`. Laravel + MySQL serializan las
 * columnas DECIMAL como strings ("3097.00"); este helper devuelve el número
 * parseado o `0` si el valor no es finito. Mismo criterio que
 * `toFiniteNumber` en `product.utils.ts`, pero con default 0 porque los
 * importes de carrito siempre deben ser sumables.
 */
function toNum(value: unknown): number {
  if (value === null || value === undefined || value === '') return 0;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Importe bruto (con IVA) de la línea = `subtotal` neto + `tax`. Se usa esta
 * suma —y no `line.total`— para que el precio del producto NO incluya el
 * descuento de código promocional: ese descuento ya se muestra aparte en la
 * fila "Descuento" del resumen.
 */
export function cartLineGrossSubtotal(line: CartLine): number {
  return toNum(line.subtotal) + toNum(line.tax);
}

/** Precio unitario bruto (con IVA, con descuento de oferta ya aplicado). */
export function cartLineUnitPrice(line: CartLine): number {
  const qty = toNum(line.quantity);
  return Math.round(cartLineGrossSubtotal(line) / Math.max(qty, 1));
}

/**
 * Porcentaje de descuento de oferta de la línea. El backend lo afirma con
 * `offer_discount` (flag) y aporta el porcentaje en `offer_discount_qty`.
 * Solo se considera válido en el rango abierto (0, 100) para no pintar un
 * badge "Oferta" hueco. El campo `discount` de `CartLine` es el monto de
 * descuento por código promo y NO debe usarse como flag.
 */
export function cartLineDiscountPct(line: CartLine): number {
  if (!line.offer_discount) return 0;
  const pct = toNum(line.offer_discount_qty);
  return pct > 0 && pct < 100 ? pct : 0;
}

/** `true` si la línea corresponde a un producto en oferta. */
export function cartLineHasOffer(line: CartLine): boolean {
  return cartLineDiscountPct(line) > 0;
}

/**
 * Precio unitario original (de lista, pre-descuento) con IVA. Se calcula
 * aplicando el IVA a `list_sale_price` (PVP de lista neto que envía el
 * backend). Si ese campo no llega, se reconstruye desde el unitario con
 * descuento. Devuelve `null` cuando la línea no está en oferta.
 */
export function cartLineOriginalUnitPrice(line: CartLine): number | null {
  const pct = cartLineDiscountPct(line);
  if (pct === 0) return null;
  const listNet = toNum(line.list_sale_price);
  if (listNet > 0) {
    return Math.round(listNet * (1 + toNum(line.taxes) / 100));
  }
  return Math.round(cartLineUnitPrice(line) / (1 - pct / 100));
}

/**
 * `true` si la línea corresponde a un single (carta individual). El backend
 * embebe el producto en `lines.product` (eager load en `getCartByToken`), así
 * que leemos su columna `product_type` directo de la relación.
 */
export function cartLineIsSingle(line: CartLine): boolean {
  return line.product?.product_type === 'single';
}

/**
 * `true` si el carrito contiene al menos un single. Cubre carritos mixtos
 * (singles + sellados/accesorios), donde `no_apply_for_intershop` no permite
 * distinguir un cart con singles de uno sin ellos.
 */
export function cartHasSingles(cart: Cart | null | undefined): boolean {
  return (cart?.lines ?? []).some(cartLineIsSingle);
}

/**
 * Espejo de `CheckoutDeliveryResolver::DELIVERY_PRODUCT_IDS` (backend,
 * `[0, 1]`): las líneas de envío/retiro que `DeliveryLineFactory` crea con
 * esos ids. Hoy `cart.lines` de esta pantalla no incluye envío (se suma
 * aparte como `deliveryPrice()` en checkout-page) — este filtro es el
 * cinturón, no la fuente principal de la exclusión (D4).
 */
const DELIVERY_PRODUCT_IDS = new Set([0, 1]);

function isDeliveryLine(line: CartLine): boolean {
  return DELIVERY_PRODUCT_IDS.has(line.product_id);
}

/**
 * Estimación frontend de mercancía sin envío: `total − Σ líneas delivery`, con clamp a 0.
 * En el checkout web actual `cart.total` representa productos y el transporte se calcula
 * aparte; el filtro cubre carritos legacy que aún arrastren una línea de envío/retiro.
 *
 * No es el mismo cálculo estructural que la API: `CheckoutDeliveryResolver::merchandiseTotal`
 * suma directamente las líneas de mercancía y es la fuente autoritativa. En un carrito
 * coherente ambos llegan al mismo importe; si divergen, el guard de la API puede rechazar
 * el apartado con 422 (docs/specs/checkout-apartados-umbral-plan.md, Nota de auditoría 2).
 */
export function cartMerchandiseTotal(cart: Cart | null | undefined): number {
  const total = toNum(cart?.total);
  const deliveryTotal = (cart?.lines ?? [])
    .filter(isDeliveryLine)
    .reduce((sum, line) => sum + toNum(line.total), 0);
  return Math.round(Math.max(0, total - deliveryTotal));
}

/**
 * Mirror en cliente de la fórmula D13 del ledger de Perrón Points (floor por
 * línea, 1 PP por cada ₡100 de `line.total` — ya neto de descuento). El
 * backend es la única fuente de verdad del acumulado real (recalcula sobre el
 * `paid` efectivo al confirmarse el pago); si los floors de línea no suman el
 * total no es un bug — la fila autoritativa manda (spec, sección Copy). Las
 * líneas de envío/retiro (si existieran en `cart.lines`) nunca pintan chip.
 */
export function cartLinePoints(line: CartLine): number {
  if (isDeliveryLine(line)) return 0;
  return Math.floor(toNum(line.total) / 100);
}

/**
 * Fila autoritativa de PP de todo el carrito: `B = cartMerchandiseTotal(cart)`,
 * `floor(B / 100)` (mismo cálculo que el backend, D13). `cart.total` ya es
 * post-descuento y hoy NUNCA incluye envío en esta pantalla (se suma aparte
 * como `deliveryPrice()` en checkout-page) — el filtro por `product_id` y el
 * clamp a 0 (dentro de `cartMerchandiseTotal`) son el cinturón defensivo, no
 * la fuente principal de la exclusión.
 */
export function cartAuthoritativePoints(cart: Cart | null | undefined): number {
  return Math.floor(cartMerchandiseTotal(cart) / 100);
}

/**
 * Mensaje de toast para un fallo de `addLine`/`updateLine`, o `null` cuando no
 * hay que decir nada.
 *
 * Existe para que los ocho call sites de `addLine` traten igual los tres casos
 * que sabe distinguir el servicio, en vez de repetir el `instanceof` cada uno:
 *  - bloqueo de Acceso VIP en el cliente (R2) → `null`: el diálogo bloqueante
 *    ya está abierto explicando el requisito, y un toast encima sería ruido;
 *  - 403 de una puerta del servidor → su copy, con la línea identificada;
 *  - 417 de stock → el mensaje del backend;
 *  - 404 de carrito ajeno o inexistente → «carrito no disponible»;
 *  - cualquier otra cosa → el `fallback` del call site.
 */
export function cartErrorMessage(err: unknown, fallback: string): string | null {
  if (err instanceof EarlyAccessBlockedError) return null;
  if (err instanceof EarlyAccessGateRejection) return err.gate.message;
  if (err instanceof CartStockError) return err.message;
  if (err instanceof CartGoneError) return CART_COPY.gone;
  return fallback;
}
